import isEqual from 'lodash/isEqual'
import { useCallback } from 'react'
import useSWR, { SWRResponse } from 'swr'

import {
  ExtractFieldValuesParams,
  ExtractionResult,
  useSageInferenceApi,
} from './sageInferenceApi'

/**
 * A hook that wraps the extractFieldValues method from SageInferenceApi with SWR.
 * This is an expensive operation, so revalidation only happens when mutate() is called.
 *
 * @param params - The parameters for field extraction, or null to skip the request
 * @returns SWRResponse with ExtractionResult data
 */
export function useSageInferenceSwr(
  params: ExtractFieldValuesParams | null
): SWRResponse<ExtractionResult, Error> {
  const { extractFieldValues } = useSageInferenceApi()

  const fetcher = useCallback(
    async (params: ExtractFieldValuesParams) => {
      return await extractFieldValues(params)
    },
    [extractFieldValues]
  )

  return useSWR<ExtractionResult, Error>(params, fetcher, {
    // Disable automatic revalidation - only revalidate on manual mutate() calls
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    revalidateOnMount: true,
    // Use deep comparison for the params key
    compare: (a, b) => isEqual(a, b),
  })
}
